// Records whether the collections that feed the local search index actually finished
// loading. Without this, a document missing from a MiniSearch result is ambiguous
// between a matching failure and a document that was never loaded in the first place.
//
// A collection is assembled from more than one request ('own' and 'allRepos' hit
// different URLs and dispatch the same success action), so outcomes are kept per
// request and summarised. Keeping them separate means a 60 second refresh re-reports
// the same request rather than adding a second copy of its count.

const EMPTY_COLLECTION = {
  ok: null, failed: 0, expected: null, at: null, requests: {},
}

const EMPTY_REQUEST = { ok: null, expected: null, at: null }

// Sets, questions and folders are the union of two requests. `ok` must stay
// null until both have reported — otherwise the first success would claim the
// collection is complete while allRepos is still in flight (or never started).
// Repos is a single unpaginated fetch, so only `own` is required.
export const REQUIRED_REQUESTS = {
  sets: ['own', 'allRepos'],
  questions: ['own', 'allRepos'],
  folders: ['own', 'allRepos'],
  repos: ['own'],
}

function summarise(collection, requests, at) {
  const required = REQUIRED_REQUESTS[collection] || Object.keys(requests)
  const entries = required.map((key) => requests[key] || EMPTY_REQUEST)
  const known = entries.filter((entry) => entry.ok !== null)
  const counted = entries.filter((entry) => entry.expected !== null)
  return {
    ok: known.length === required.length ?
      entries.every((entry) => entry.ok === true) :
      null,
    failed: entries.filter((entry) => entry.ok === false).length,
    // Same rule as ok: a partial sum is the size of one slice, not the
    // collection, and would make indexed >= expected look complete too early.
    expected: counted.length === required.length ?
      counted.reduce((total, entry) => total + entry.expected, 0) :
      null,
    at,
    requests,
  }
}

function updateRequest(state, collection, request, changes) {
  const previous = state[collection] || EMPTY_COLLECTION
  const previousRequest = previous.requests[request] || EMPTY_REQUEST
  const at = new Date().toISOString()
  const requests = {
    ...previous.requests,
    [request]: { ...previousRequest, ...changes, at },
  }
  return { ...state, [collection]: summarise(collection, requests, at) }
}

export default function (state = {}, action) {
  switch (action.type) {
    case 'LIBRARY_LOAD_STATUS': {
      const {
        collection, request, ok, expected,
      } = action
      // a failed request tells us nothing new about the size of the collection, and
      // a delta request carries no count, so in both cases the last known count stands
      const changes = (expected === null || expected === undefined) ?
        { ok } :
        { ok, expected }
      return updateRequest(state, collection, request, changes)
    }

    case 'LIBRARY_LOAD_EXPECTED': {
      const { collection, request, expected } = action
      if (expected === null || expected === undefined) return state
      return updateRequest(state, collection, request, { expected })
    }

    case 'LOGOUT':
      return {}

    default:
      return state
  }
}
